class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    if (parsedState.id) this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Handle progress bar update after rendering (using global function)
    if (parsedState && typeof updateFreeGiftProgressBar === 'function') {
        updateFreeGiftProgressBar(parsedState);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);



/* ================================
   🔥 CONFIG
================================ */
const FREE_GIFT_VARIANT_ID = 44074855727164;
const MIN_CART_TOTAL_FOR_GIFT = 54900; // ₹549


/* ================================
   🎁 ADD / REMOVE FREE GIFT (ROBUST)
================================ */
async function checkFreeGift(cartData = null) {
  if (window.isUpdatingFreeGift) return;

  try {
    let cart = cartData;
    // If cartData doesn't look like a cart object or is missing items, fetch it.
    if (!cart || !cart.items) {
      console.log("Fetching latest cart for gift check...");
      cart = await fetch('/cart.js?v=' + new Date().getTime()).then(r => r.json());
    }

    // Update progress bar UI (using global function)
    if (typeof updateFreeGiftProgressBar === 'function') {
      updateFreeGiftProgressBar(cart);
    }

    const totalWithoutGift = cart.items.reduce((total, item) => {
      const isGift = item.properties && (item.properties._free_gift === "true" || item.properties["_free_gift"] === "true");
      if (isGift) return total;
      return total + item.final_line_price;
    }, 0);

    const qualifies = totalWithoutGift >= MIN_CART_TOTAL_FOR_GIFT;
    const giftItem = cart.items.find(item => item.properties && (item.properties._free_gift === "true" || item.properties["_free_gift"] === "true"));

    console.log("Free Gift Check Status:", {
      totalWithoutGift: totalWithoutGift / 100,
      qualifies: qualifies,
      giftItemFound: giftItem ? "Yes" : "No",
      giftKey: giftItem ? giftItem.key : null
    });

    /* ======================
       ADD FREE GIFT
    ====================== */
    if (qualifies && !giftItem) {
      console.log("Automatically adding free gift...");
      window.isUpdatingFreeGift = true;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: FREE_GIFT_VARIANT_ID,
          quantity: 1,
          properties: { "_free_gift": "true" },
          sections: "cart-drawer,cart-icon-bubble"
        })
      });
      const result = await response.json();
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && result.sections) {
        cartDrawer.renderContents({ id: result.token, sections: result.sections });
      }
      window.isUpdatingFreeGift = false;
    }

    /* ======================
       REMOVE FREE GIFT
    ====================== */
    else if (!qualifies && giftItem) {
      console.log("Automatically removing free gift...");
      window.isUpdatingFreeGift = true;

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0,
          sections: "cart-drawer,cart-icon-bubble"
        })
      });
      const result = await response.json();
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && result.sections) {
        cartDrawer.renderContents({ id: result.token, sections: result.sections });
      }
      window.isUpdatingFreeGift = false;
    }

  } catch (err) {
    console.error("Free gift logic error:", err);
    window.isUpdatingFreeGift = false;
  }
}


/* ================================
   🚀 EVENTS
================================ */

console.log("Cart Drawer Free Gift Script Loaded");
window.isUpdatingFreeGift = false;

async function onCartUpdate(eventData) {
  console.log("Cart Update Event Received:", eventData);
  if (window.isUpdatingFreeGift) return;
  const cart = (eventData && eventData.cartData && eventData.cartData.items) ? eventData.cartData : null;
  await checkFreeGift(cart);
}

// Subscribe to cart updates
if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
  subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
}

// Page load check
document.addEventListener("DOMContentLoaded", () => checkFreeGift());

// Fallbacks for various cart actions
document.addEventListener("submit", function (e) {
  if (e.target.action && e.target.action.includes('/cart/add')) {
    setTimeout(() => checkFreeGift(), 1500);
  }
});

// Extra listener for remove buttons and quantity buttons
document.addEventListener("click", function (e) {
  const target = e.target.closest('.cart-remove-button, .quantity__button');
  if (target) {
    console.log("Cart interaction detected (Click), scheduling robust checks...");
    // Multiple checks to handle server-side processing time
    setTimeout(() => checkFreeGift(), 500);
    setTimeout(() => checkFreeGift(), 1500);
    setTimeout(() => checkFreeGift(), 3000);
  }
});

// Update for quantity input changes
document.addEventListener("change", function (e) {
  if (e.target.matches('.quantity__input')) {
    console.log("Quantity change detected, scheduling robust checks...");
    setTimeout(() => checkFreeGift(), 500);
    setTimeout(() => checkFreeGift(), 2000);
  }
});